import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Stack } from "react-bootstrap";
import { useMediaQuery } from 'react-responsive';

import { getDeletedRecords } from "../utils/Database";
import { applyFilter } from "../utils/ApplyFilter";
import { showToast } from "../utils/Toasts";
import { sortScripts } from '../utils/SortScripts';
import { formatDate } from "../utils/Dates";

import { FilterContext } from "../utils/Contexts";

import { SearchText } from "../components/SearchFields";
import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";
import SortableHeader from "../components/SortableHeader";
import PageList from "../components/PageList";
import { RestoreConfirmationModal } from "../components/Confirmation";

import adminStore from "../stores/Admin";

const ITEMS_PER_PAGE = 100;

export default function DeletedRecords() {
    const navigate = useNavigate();
    const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
    const [loading, setLoading] = useState(true);
    const [deletedScripts, setDeletedScripts] = useState([]);
    const [shownScripts, setShownScripts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({});
    const [filteredScriptCount, setFilteredScriptCount] = useState(0);

    const [fileInfo, setFileInfo] = useState<any>(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);

    // Subscribe to admin store
    const sortConfig = adminStore((state) => state.getSortConfig("deleted"));
    const setSortConfig = adminStore((state) => state.setSortConfig);
    const setActiveTab = adminStore((state) => state.setActiveTab);

    // Set active tab to "deleted" when component mounts
    useEffect(() => {
        setActiveTab("deleted");
    }, [setActiveTab]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            } else {
                return { key, direction: 'asc' };
            }
        });
    };

    const fetchDeletedRecords = async () => {
        setLoading(true);
        const records = await getDeletedRecords();
        if (records) {
            setDeletedScripts(records);
        } else {
            showToast("error", "Failed to fetch deleted records.");
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchDeletedRecords();
    }, []);

    const sortedScripts = useMemo(() => {
        return sortScripts(deletedScripts, sortConfig);
    }, [deletedScripts, sortConfig]);

    useEffect(() => {
        const results = applyFilter(sortedScripts, filters);
        setFilteredScriptCount(results.length);
        
        setShownScripts(
            results.length > ITEMS_PER_PAGE ?
                results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                : results
        );
    }, [currentPage, sortedScripts, sortConfig, filters]);

    return (
        <>
            <FilterContext value={{ filters: filters, setFilters: setFilters, search: {} }}>
                <Stack direction={isSmall ? "vertical" : "horizontal"} gap={3} className="d-flex">
                    <SearchText fieldName="Script Title" className="flex-grow-1" />
                    <SearchText fieldName="Writer(s)" className="flex-grow-1" />
                    <SearchText fieldName="additional_info_concat" placeholder="Additional Info" className="flex-grow-1" />
                    <SearchText fieldName="flag_reason" placeholder="Flag Reason" className="flex-grow-1" />
                </Stack>
            </FilterContext>
            <Stack direction={isSmall ? "vertical" : "horizontal"} gap={3} className="d-flex">
                {loading ? (
                    <LoadingText text="Loading deleted records..." className="mt-2" />
                ) : (
                        <p className="mt-2"><strong>{shownScripts.length}</strong> out of <strong>{filters ? filteredScriptCount : deletedScripts.length}</strong> records shown</p>
                )}
                <PageList
                    className={isSmall ? "justify-content-center" : "ms-auto"}
                    totalCount={filters ? filteredScriptCount : deletedScripts.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                />
            </Stack>
            {loading? (
                <LoadingRing />
            ) : (
                <div className="script-table table-container mt-3 mb-4">
                    <div className="table-responsive">
                        {shownScripts.length > 0 ? (
                            <Table striped hover size="sm">
                                <thead>
                                    <tr>
                                        <SortableHeader
                                            label="Script Title"
                                            sortKey="Script Title"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                            minWidth={200}
                                        />
                                        <SortableHeader
                                            label="Writer(s)"
                                            sortKey="Writer(s)"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                            minWidth={150}
                                        />
                                        <SortableHeader
                                            label="Additional Info"
                                            sortKey="additional_info_concat"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                            minWidth={200}
                                        />
                                        <SortableHeader
                                            label="Draft Date"
                                            sortKey="Draft Date"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                        />
                                        <SortableHeader
                                            label="Date Added"
                                            sortKey="Date Added"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                        />
                                        <SortableHeader
                                            label="Flag Reason"
                                            sortKey="flag_reason"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                            width={300}
                                        />
                                        <SortableHeader
                                            label="Last Updated"
                                            sortKey="last_updated"
                                            sortConfig={sortConfig}
                                            onSort={handleSort}
                                        />
                                        <th
                                            style={{ width: "200px" }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shownScripts
                                        .map((script) => {
                                            return (
                                                <tr key={script.file_id}>
                                                    <td className="h6" key={"title_" + script.file_id}>
                                                        <a
                                                            href={`/database/script/${script.file_id}`}
                                                            onClick={e => {
                                                                e.preventDefault(); // Prevent default anchor navigation
                                                                navigate(`/database/script/${script.file_id}`, { state: { from: "admin" } }); // Use SPA navigation
                                                            }}
                                                        >
                                                            {script["Script Title"]}
                                                        </a>
                                                    </td>
                                                    <td className="h6" key={"wr_" + script.file_id}>
                                                        {script["Writer(s)"]}
                                                    </td>
                                                    <td className="h6" key={"ai_" + script.file_id}>
                                                        {script.additional_info_concat}
                                                    </td>
                                                    <td className="h6" key={"dd_" + script.file_id}>
                                                        {script["Draft Date"]}
                                                    </td>
                                                    <td className="h6" key={"da_" + script.file_id}>
                                                        {script["Date Added"]}
                                                    </td>
                                                    <td className="h6 text-danger" key={"fr_" + script.file_id}>
                                                        {script.flag_reason}
                                                    </td>
                                                    <td className="h6" key={"lu_" + script.file_id}>
                                                        {formatDate(script.last_updated)}
                                                    </td>
                                                    <td key={"edit_" + script.file_id} className="text-center">
                                                        <Stack direction="horizontal" gap={2}>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    navigate(`/database/script/${script.file_id}/edit`, { state: { from: "admin" } });
                                                                }}
                                                            >
                                                                <i className="bi bi-pencil-square me-2" aria-label="Edit" title="Edit"></i>Edit
                                                            </Button>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setFileInfo(script);
                                                                    setShowRestoreModal(true);
                                                                    setPendingAction(() => async () => {
                                                                        setShowRestoreModal(false);
                                                                        setFileInfo(null);
                                                                        fetchDeletedRecords();
                                                                    });
                                                                }}
                                                            >
                                                                <i className="bi bi-arrow-counterclockwise me-2"></i>Restore
                                                            </Button>
                                                        </Stack>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No records found.</p>
                        )}
                    </div>
                </div>
            )}
            <RestoreConfirmationModal
                show={showRestoreModal}
                fileInfo={fileInfo}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowRestoreModal(false);
                    setPendingAction(null);
                }}
            />
        </>
    );
}